import React, { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import Container from "@mui/material/Container";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { SearchSection } from "./SearchSection";
import { TopicsGrid } from "./TopicsGrid";
import { InputSection } from "./InputSection";
import { ChatContainer } from "./ChatContainer";
import axios from "axios";
import { useRecoilValue, useRecoilCallback } from "recoil";
import { atom_tourState } from "@app/shared/state/tour.atom";
import { atom_geolocationState } from "@app/shared/state/geolocation.atom";
import { useTranslation } from "react-i18next";
import { ArrowBack } from "@mui/icons-material";

// Define message interface
interface Message {
  id: string;
  type: "user" | "assistant";
  content: string;
}

// Create a theme instance
const theme = createTheme({
  typography: {
    fontFamily: '"Rubik", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  palette: {
    primary: {
      main: '#D0552E',
    },
  },
});

export default function SmartAgent({ onBackToMapClicked }: { onBackToMapClicked: () => void }) {
  const [showSearch, setShowSearch] = useState(true);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const activeTour = useRecoilValue(atom_tourState);
  const { t } = useTranslation();
  const { uID } = JSON.parse(localStorage.getItem('credential_items') || '{}');

  // Read geolocation on-demand without subscribing (prevents re-renders)
  const getGeolocation = useRecoilCallback(({ snapshot }) => () => {
    return snapshot.getLoadable(atom_geolocationState).getValue();
  }, []);

  const handleSendMessage = async (content: string) => {
    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      type: "user",
      content
    };

    setMessages(prev => [...prev, userMessage]);
    setShowSearch(false);

    // Set loading state
    setLoading(true);

    // Read geolocation when sending message
    const geolocation = getGeolocation();

    // Call axios request
    const data = JSON.stringify({
      "lat": geolocation?.latitude,
      "lng": geolocation?.longitude,
      "uID": uID,
      "tourName": activeTour?.title,
      "userQuestion": content,
      "tourID": activeTour?.id
    });

    const config = {
      method: 'post',
      maxBodyLength: Infinity,
      url: 'https://europe-west3-tours-app-1579553856346.cloudfunctions.net/AIagent',
      headers: {
        'Content-Type': 'application/json'
      },
      data: data
    };

    await axios.request(config)
      .then((response) => {
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          type: "assistant",
          content: response.data?.answer
        };
        setMessages(prev => [...prev, assistantMessage]);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        // Remove loading state
        setLoading(false);
      });

    // Scroll to bottom
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  };

  const handleSelectTopic = (topic: string) => {
    handleSendMessage(topic);
  };

  // This effect sets the initial predefined message for the example
  useEffect(() => {
    // if (messages.length === 0) {
    //   setMessages([
    //     {
    //       id: "1",
    //       type: "user",
    //       content: "I'm looking for a culinary experience in the city with recommendations for unique restaurants."
    //     },
    //     {
    //       id: "2",
    //       type: "assistant",
    //       content: "No problem, I'll find it for you soon."
    //     }
    //   ]);
    //   setShowSearch(false);
    // }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container
        disableGutters
        sx={{
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
          bgcolor: "white",
          m: 0,
          touchAction: 'manipulation', // Prevent zooming on double-tap
          "& *": {
            touchAction: 'manipulation'
          },
          "& input, & textarea": {
            fontSize: "16px !important",
            transform: "scale(1) !important",
            transformOrigin: "0 0 !important"
          }
        }}
      >
        <Box
          component="header"
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: `${activeTour?.config?.['chatColor'] || "#801D4A"}`,
            color: "white",
            py: 3,
            position: "relative",
            flexShrink: 0
          }}
        >
          <Button
            variant="outlined"
            sx={{
              position: "absolute",
              left: 0,
              color: "#fff",
              fontSize: "12px",
              fontWeight: "bold",
              textDecoration: "underline",
              textTransform: "none",
              border: "1px solid rgba(255, 255, 255, 0.5)",
              borderRadius: "14px",
              "&:hover": {
                color: "#fff",
                bgcolor: "transparent"
              }
            }}
            onClick={onBackToMapClicked}
          >
            {t('Back to Map')}
          </Button>
          <Box sx={{ fontSize: "18px", fontWeight: "bold", position: "absolute", left: "50%", transform: "translateX(-50%)" }}>
            {t('Smart Guide')}
          </Box>
          <Box sx={{ width: "73px" }}></Box> {/* Spacer to balance the button on the left */}
        </Box>

        <Box
          component="main"
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2 },
            overflow: "hidden"
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, flexGrow: 1, overflow: "hidden" }}>

            {showSearch ? (
              <>
                <SearchSection />
                <TopicsGrid onSelectTopic={handleSelectTopic} />

                {messages.length === 0 && (
                  <Box ref={containerRef} sx={{ mt: 'auto', width: '100%', flexShrink: 0 }}>
                    <InputSection onSendMessage={handleSendMessage} />
                  </Box>
                )}
              </>
            ) : (
              <ChatContainer messages={messages} loading={loading} />
            )}
          </Box>
          {messages.length > 0 && (
            <Box ref={containerRef} sx={{ mt: 'auto', width: '100%', flexShrink: 0 }}>
              <InputSection onSendMessage={handleSendMessage} />
            </Box>
          )}
        </Box>
      </Container>
    </ThemeProvider>
  );
}

